import { useCallback, useMemo, useState } from "react";
import type { ProviderKind, ServerProvider } from "@cmmd-forge/contracts";
import { resolveDefaultProviderAccountId } from "../../cmmd/forgeProviderAccess";

import { buildComposerModelOptionsByProvider } from "../chat/ChatComposer.logic";
import { resolveNewThreadModelSelection, resolvePreferredModel } from "./NewThreadLanding.logic";
import {
  resolveProvisioningModelSelection,
  type ProvisioningProviderCatalogEntry,
} from "./provisioningProviderCatalog";

type ModelSelectionInput = Parameters<typeof resolveNewThreadModelSelection>[0];
type OptionsInput = Parameters<typeof buildComposerModelOptionsByProvider>;

/**
 * Which model the first turn will use.
 *
 * Sticky per provider, not global: a user who has chosen Sonnet for Claude and
 * a different model for Codex expects both remembered, so switching provider
 * restores that provider's model rather than clearing the choice. That is why
 * the state is keyed by provider and the selection is derived rather than
 * stored — a stored selection would go stale the moment the eligible set
 * changed underneath it.
 */
export function useDraftModelSelection(input: {
  readonly providerStatuses: ModelSelectionInput["providers"];
  readonly providerAccess: ModelSelectionInput["providerAccess"];
  readonly providerCredentialMode: ModelSelectionInput["credentialMode"];
  readonly orgProviderPolicy: OptionsInput[1];
  readonly runtimeInventoryKnown: boolean;
  readonly provisioningProviderCatalog: ReadonlyArray<ProvisioningProviderCatalogEntry> | null;
}) {
  const {
    providerStatuses,
    providerAccess,
    providerCredentialMode,
    orgProviderPolicy,
    runtimeInventoryKnown,
    provisioningProviderCatalog,
  } = input;

  const [selectedProvider, setSelectedProvider] = useState<ProviderKind | null>(null);
  const [selectedModelByProvider, setSelectedModelByProvider] = useState<
    Partial<Record<ProviderKind, string>>
  >({});
  const [selectedProviderAccountByProvider, setSelectedProviderAccountByProvider] = useState<
    Partial<Record<"codex" | "claudeAgent", string>>
  >({});

  const selectedModelSelection = useMemo(() => {
    const preferredModel = resolvePreferredModel(selectedProvider, selectedModelByProvider);
    const selection =
      !runtimeInventoryKnown && provisioningProviderCatalog !== null
        ? resolveProvisioningModelSelection({
            catalog: provisioningProviderCatalog,
            preferredProvider: selectedProvider,
            preferredModel,
          })
        : resolveNewThreadModelSelection({
            providers: providerStatuses,
            preferredProvider: selectedProvider,
            preferredModel,
            providerAccess,
            credentialMode: providerCredentialMode,
          });
    if (
      selection === null ||
      (selection.provider !== "codex" && selection.provider !== "claudeAgent")
    ) {
      return selection;
    }
    const providerAccountId =
      selectedProviderAccountByProvider[selection.provider] ??
      resolveDefaultProviderAccountId(providerAccess, selection.provider);
    return providerAccountId ? { ...selection, providerAccountId } : selection;
  }, [
    providerAccess,
    providerCredentialMode,
    providerStatuses,
    provisioningProviderCatalog,
    runtimeInventoryKnown,
    selectedModelByProvider,
    selectedProvider,
    selectedProviderAccountByProvider,
  ]);

  /**
   * Options for every published provider, not only the ones this person may
   * select. A Withheld Provider's models render greyed, so a person can see
   * what they would get once it is usable; the picker refuses the click and the
   * selection above still fails closed on eligibility.
   */
  const modelOptionsByProvider = useMemo<
    Record<ProviderKind, ReadonlyArray<ServerProvider["models"][number]>>
  >(
    () =>
      buildComposerModelOptionsByProvider(
        !runtimeInventoryKnown && provisioningProviderCatalog !== null
          ? provisioningProviderCatalog
          : providerStatuses,
        orgProviderPolicy,
      ),
    [orgProviderPolicy, providerStatuses, provisioningProviderCatalog, runtimeInventoryKnown],
  );

  const selectProviderModel = useCallback((provider: ProviderKind, model: string) => {
    setSelectedProvider(provider);
    setSelectedModelByProvider((previous) => ({ ...previous, [provider]: model }));
  }, []);

  const selectProviderAccount = useCallback(
    (provider: "codex" | "claudeAgent", providerAccountId: string) => {
      setSelectedProvider(provider);
      setSelectedProviderAccountByProvider((previous) => ({
        ...previous,
        [provider]: providerAccountId,
      }));
    },
    [],
  );

  return {
    selectedProvider,
    setSelectedProvider,
    selectedModelByProvider,
    setSelectedModelByProvider,
    selectedProviderAccountByProvider,
    selectedModelSelection,
    resolvedSelectedProvider: selectedModelSelection?.provider ?? "codex",
    selectedModel: selectedModelSelection?.model ?? "",
    modelOptionsByProvider,
    selectProviderModel,
    selectProviderAccount,
  };
}
